import { client } from 'graphql/client';

import isEmpty from 'lodash/isEmpty';
import type {
  User,
  UserAction,
  UserResponse,
  UserMFAResponse,
  Interest,
  AuthMobileResponse,
  TokenExpiryTime,
  TokenExpiryTimeParams,
} from './types';
import type { Dispatch, ThunkedAction, AppState, Action } from 'globals/types';
import { showToast, resetToast } from 'toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import fetch from 'globals/wa-fetch';
import getErrorMessage from 'constants/error-messages';
import {
  getSubdomainFromHost,
  goTo,
  getUrlParameter,
  getCurrentState,
} from 'router/navigation';
import {
  setCompany,
  fetchCompany,
  setPreLoginCompanyDetails,
} from 'stores/company-store';
import { getUser, isSharedAccount } from 'stores/user-store';
import { startPostLoginFlow } from 'state/post-login/actions';
import {
  handleLoginRedirect,
  setSubDomainCookie,
  isSSO,
  getSSOLogoutLink,
  clearLogoutStorage,
  getToken,
} from './helpers';
import { get as storageGet, storageKeys } from 'globals/storage';
import { updateFeaturesList } from 'state/features-list/actions';
import Language from 'language';
import { updateMenu } from 'state/menu/actions';
import { initialiseCompany } from 'state/global/actions';
import extend from 'lodash/extend';
import { signupStates } from 'router/constants';
import { getStore } from 'app/hermes-redux';
import { sessionInvalidationTypes } from 'wa-storybook/components/modal/session-invalidation-alert-modal/constants';
import type { SessionInvalidationTypes } from 'wa-storybook/components/modal/session-invalidation-alert-modal/types';
import { SIGNUP_CODES } from '../../pages/signup/constants';
import { setHotjarUserAttributes } from 'globals/hotjar/helpers';
import { setExperimentAttributes } from 'globals/experiments/experiments';
import {
  getStoredSelectedEnvironment,
  removeStoredSelectedEnvironment,
} from 'react-components/env-controller/env-controller-context';
import { isUnifiedLoginEnabled } from 'globals/testable';
import {
  AppLogger,
  LogTypes,
  AuthClient,
  Localisation,
  CORRELATION_ID_KEY,
} from '@workivate/tho-web-shared';
import { setSuppressAuthErrors } from 'run-with-usl.auth-latches';
import { runOnWebWorker } from 'app/run-on-web-worker';
import {
  errorCodes,
  parseErrorCode,
} from 'wa-storybook/global/constants/error-messages';

export const setUser = (user: User): UserAction => {
  setHotjarUserAttributes({ locale: user.locale });

  return { type: 'SESSION/SET_USER', newUser: user };
};

export const resetUser = (): UserAction => {
  return { type: 'SESSION/RESET_USER' };
};

export function updateUserSuccess(user: User): UserAction {
  setHotjarUserAttributes({ locale: user.locale });

  return { type: 'SESSION/UPDATE_USER_SUCCESS', newUser: user };
}

export function updateUser(
  newUser: any,
  signup?: boolean | null,
  shouldShowSuccessToast = true,
): ThunkedAction {
  return function (dispatch: Dispatch): Promise<any> {
    dispatch({ type: 'SESSION/UPDATE_USER_REQUEST' });
    const req = {
      body: undefined,
      method: isSharedAccount() ? 'get' : 'put', // never update shared account !
      version: 1.3,
      [signup ? 'signupToken' : 'token']: true,
    };

    // internet explorer edge doesnt allow empty body on get request
    if (!isSharedAccount()) {
      // never update shared account !
      req.body = newUser;
    }

    return fetch('/user/me?exclude_field=badges', req).then(
      (response: UserResponse & UserMFAResponse) => {
        if (response.body.signup_state === SIGNUP_CODES.MFA_SETUP_REQUIRED) {
          return response;
        }

        if (shouldShowSuccessToast && !signup) {
          dispatch(
            showToast({
              type: ToastType.SUCCESS,
              message: polyglot.t('profile_edit_page.controller.toaster'),
            }),
          );
        }

        return dispatch(updateUserSuccess(response.body));
      },
      (response: UserResponse) => {
        AppLogger.logWithOptions({
          error: response?.error,
          message: 'Error during signup form submission',
          level: LogTypes.Error,
          shouldLogToDatadog: true,
          shouldLogToDynatrace: true,
        });

        const errorMessage =
          response?.error?.code ===
          parseErrorCode(errorCodes['sso_email_exists'][0])
            ? getErrorMessage(response.error?.code)
            : window.polyglot.t('sign_up.error');

        dispatch(
          showToast({
            type: ToastType.ERROR,
            message: errorMessage,
          }),
        );

        dispatch({ type: 'SESSION/UPDATE_USER_ERROR', response });

        return Promise.reject(response);
      },
    );
  };
}

export function changeInterest(interest: Interest): ThunkedAction {
  return function (dispatch: Dispatch) {
    dispatch({ type: 'SESSION/CHANGE_INTEREST', interest: interest });
  };
}

export const setLoadingLanguageStarted = (): Action => ({
  type: 'SESSION/LOADING_LANGUAGE_STARTED',
});

export const setLoadingLanguageCompleted = (): Action => ({
  type: 'SESSION/LOADING_LANGUAGE_COMPLETED',
});

export const setSessionLoadingStarted = (): Action => ({
  type: 'SESSION/LOADING_STARTED',
});

export const setSessionLoadingCompleted = (): Action => ({
  type: 'SESSION/LOADING_COMPLETED',
});

export const updateLanguage =
  (newLocale: string, isUserLoggedIn = true) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoadingLanguageStarted());
    await client.clearStore();
    try {
      await Language.set({
        locale: newLocale,
        shouldLoadAllTranslations: true,
        resetGlobalContent: true,
      });

      AuthClient.lang = Localisation.normaliseLocale(newLocale);

      if (isUserLoggedIn) {
        // re-rendering menu (AFTER Language.set) to update
        // 1. Pension and Benefits
        // 2. "Language: .... (..)"
        // profile menu items to the new language
        dispatch(updateMenu(getUser().features_list));
      } else if (getCurrentState().name !== signupStates.signup) {
        await setPreLoginCompanyDetails(newLocale);
      }

      dispatch(
        showToast({
          message: polyglot.t('settings.translations.success'),
          type: ToastType.SUCCESS,
        }),
      );
    } catch {
      dispatch(
        showToast({
          message: polyglot.t('settings.translations.error'),
          type: ToastType.ERROR,
        }),
      );
    } finally {
      dispatch(setLoadingLanguageCompleted());
    }
  };

export const resetInterestList = (): ThunkedAction => {
  return function (dispatch: Dispatch) {
    dispatch({ type: 'SESSION/RESET_INTERESTS' });
  };
};

export function saveInterests(selectedInterests: Array<string>): ThunkedAction {
  return function (dispatch: Dispatch) {
    dispatch(updateUser({ interest_list: selectedInterests }))
      .then(() => {
        return dispatch(
          showToast({
            type: ToastType.SUCCESS,
            message: polyglot.t('profile.interests.saved'),
          }),
        );
      })
      .catch((res: any) =>
        dispatch(
          showToast({
            type: ToastType.ERROR,
            message: getErrorMessage(res.error.code),
          }),
        ),
      );
  };
}

export const login = (response: AuthMobileResponse) => {
  return async (dispatch: Dispatch) => {
    const langQueryString = getUrlParameter('lang');

    if (response === null) {
      return Promise.reject({ error: 'lifeworks-portal' });
    }

    const { user, company } = response;
    const subdomain = getSubdomainFromHost();
    const companySubdomain = company['wa_subdomain'];

    // always set a subdomain cookie, in case in future
    // they visit on a different subdomain
    setSubDomainCookie(companySubdomain);

    // Redirection
    if (!companySubdomain || subdomain !== companySubdomain) {
      setCompany(company);
      handleLoginRedirect(companySubdomain);
      return Promise.reject({ error: 'redirecting' });
    }

    if (
      Language.getLoginLanguagePersistence() &&
      Language.getLocale() !== user.locale
    ) {
      if (!user.is_shared_account) {
        dispatch(updateUser({ locale: Language.getLocale() }, false, false));
      }

      Language.loadTranslations(Language.getLocale());
    }

    dispatch(setUser(user));

    if (user.features_list.length !== 0) {
      getStore().dispatch(updateFeaturesList(user.features_list));
    }

    setCompany(company);

    if (!Language.getLoginLanguagePersistence()) {
      // we need to set the locale before continuing from here
      await Language.set({
        locale: langQueryString || response.user.locale,
        isSharedAccount: response.user.is_shared_account,
        shouldLoadAllTranslations: true,
      });
    }

    runOnWebWorker(async () => {
      // we need a new company with the correct locale assets
      // because before logging in we don't have the user's locale
      const { body: newCompany } = await fetchCompany(
        (response.company || {})['company_id'],
      );

      setCompany(newCompany, true);
    });

    runOnWebWorker(async () => {
      // we need to set up the language first then re fetch the user with correct lw-language header
      const options = {
        token: true,
        version: 1.3,
      } as const;

      const { body: newUser } = await fetch(
        '/user/me?exclude_field=badges',
        options,
      );

      dispatch(setUser(extend({}, newUser)));
    });

    runOnWebWorker(startPostLoginFlow);
  };
};

export const setIsLoggingOut = (isLoggingOut: boolean) => ({
  type: 'SESSION/IS_LOGGING_OUT',
  payload: isLoggingOut,
});

export const commonLogoutCleanup = ({ dispatch }: { dispatch: Dispatch }) => {
  const setEnvironment = getStoredSelectedEnvironment();
  clearLogoutStorage();

  getStore().dispatch(resetToast());
  getStore().dispatch(initialiseCompany(false));

  dispatch({ type: 'SESSION/LOGOUT_USER' });
  setExperimentAttributes({
    id: undefined,
    companyId: undefined,
    companyName: undefined,
  });

  if (setEnvironment) {
    removeStoredSelectedEnvironment();
  }
};

/**
 * isHard determines whether to call backend logout endpoint:
 * - true: Session is still valid on backend, call logout endpoint to end it
 * - false: Session already ended on backend (e.g., expired), skip backend call
 * This applies to both USL and non-USL flows
 */
export type LogoutParams = {
  isHard: boolean;
  sessionInvalidationType?: SessionInvalidationTypes | null;
  stayOnTheSamePage?: boolean;
};

/** Guard against multiple logout calls either at the same time or in quick succession.
 * This can happen when multiple 401 responses are received or when logout is called explicitly multiple times.
 * There is a delay before it is reset to ensure the Session Invalidation Modal can be shown correctly
 * and to ensure all network calls have completed.
 * It is triggered by URL parameters so any subsequent navigation would hide it.
 */
let canLogout = true; // use to prevent duplicate logout calls (Redux state is not immediately updated)

export const logout = ({
  isHard,
  sessionInvalidationType = null,
  stayOnTheSamePage = false,
}: LogoutParams): ThunkedAction => {
  return async (dispatch: Dispatch, getState: () => AppState): Promise<any> => {
    const { isLoggingOut } = getStore().getState().session; // can be used by other components to check state but is not immediate
    if (!canLogout || isLoggingOut) {
      return;
    }

    canLogout = false; // needs to be correct immediately in case of multiple simultaneous 401s
    dispatch(setIsLoggingOut(true)); // used in UI
    dispatch(setSessionLoadingStarted());

    try {
      let cleanupPerformed = false;

      // Build query params for session invalidation types
      const tokenExpiryTime: TokenExpiryTime | null = storageGet(
        storageKeys.tokenExpiryTime,
        true,
      );

      let queryParams: Partial<TokenExpiryTimeParams> = {};

      /*
       * When isHard=false (soft logout - session already ended on backend)
       * we handle session invalidation types by setting query params
       * for the redirect to login page to show the SessionInvalidationAlertModal.
       */
      if (
        tokenExpiryTime &&
        sessionInvalidationType === sessionInvalidationTypes.sessionTimeout
      ) {
        queryParams = {
          timeoutAmount: tokenExpiryTime.amount,
          timeoutUnit: tokenExpiryTime.unit,
        };
      } else if (
        sessionInvalidationType ===
        sessionInvalidationTypes.authMethodOrMFAChange
      ) {
        queryParams = {
          isGenericInvalidation: 'true',
        };
      }

      if (isUnifiedLoginEnabled()) {
        /*
         * AuthClient.signOut() always cleans up browser storage and AuthClient session.
         * If isHard=true, AuthClient.signOut() will:
         * - end the auth session by calling the oAuth2 /logout endpoint
         * - perform a top level redirect to the logout URL, if configured (e.g. for SSO companies)
         * We need to ensure that:
         * 1. the cleanup is performed before the redirect
         * 2. the session loading state is set to true, so the user doesn't see the login page before the redirect happens
         * 3. logout reason is handled
         * Where the session has already ended, as determined by 401 response from refreshTokens
         * we do not want to log out from back end because the session is already ended and that can cause errors.
         * So when isHard=false we want to:
         * - call AuthClient.signOut() with isHard=false
         * - handle any redirect in Hermes front end, including session invalidation type
         */

        // Always perform cleanup when Unified Login is enabled
        commonLogoutCleanup({ dispatch });
        cleanupPerformed = true;

        /*
         * Suppress auth errors while AuthClient.signOut() is running so that any
         * auth:error events it emits (e.g. if the /logout endpoint fails) are not
         * misinterpreted as unexpected auth errors and shown as TroubleshootingPanel.
         */
        setSuppressAuthErrors(true);

        try {
          await AuthClient.signOut({
            isHard,
            lang: Localisation.normaliseLocale(Language.getLocale()),
          });
        } finally {
          /*
           * Reset the suppression flag once signOut() has completed (or thrown).
           * For hard logout and soft logout with navigation the page reloads and
           * the module re-initialises anyway, but for the stayOnTheSamePage path
           * the page stays alive and we must reset the flag so that auth errors
           * from a subsequent login attempt are not silently swallowed.
           */
          setSuppressAuthErrors(false);
        }

        // Handle frontend redirects when isHard is false
        if (!isHard) {
          const logoutRedirectUrl = getState().companySettings.logoutUrl;

          if (isSSO()) {
            const logoutUrl = getSSOLogoutLink(Language.getLocale());
            if (logoutUrl && !stayOnTheSamePage) {
              window.location.href = logoutUrl;
              return;
            }
          } else if (logoutRedirectUrl && !stayOnTheSamePage) {
            window.location.href = logoutRedirectUrl;
            return;
          }

          // Fallback redirect when no custom URL and not staying on page
          if (!stayOnTheSamePage) {
            // Reset state before navigation
            dispatch(setIsLoggingOut(false));
            dispatch(setSessionLoadingCompleted());

            goTo('/', queryParams, { stateParams: { isLoggingOut: true } });
            return;
          }

          // If staying on page (isHard: false, stayOnTheSamePage: true)
          // Reset loading state immediately
          dispatch(setIsLoggingOut(false));
          dispatch(setSessionLoadingCompleted());
          // Don't return - let WAM block run for cleanup
        } else {
          // isHard: true - backend will redirect
          // Set a safety timeout in case redirect fails
          setTimeout(() => {
            dispatch(setIsLoggingOut(false));
            dispatch(setSessionLoadingCompleted());
          }, 3000);
          // Don't return - let WAM block run for cleanup if this is a Sign Up flow
        }
      }

      // if a shared user is creating a limited account, we will have both oAuth2 and WAM tokens in storage
      // so we need to check for a WAM token even when USL is enabled
      if (!isEmpty(getToken())) {
        try {
          const logoutRedirectUrl = getState().companySettings.logoutUrl;
          await client.clearStore();

          if (!sessionInvalidationType && isHard) {
            await fetch('/auth/mobile', {
              method: 'delete',
              token: true,
              version: 1.3,
            });
          }

          if (!cleanupPerformed) {
            commonLogoutCleanup({ dispatch });
          }

          // if Unified Login is enabled, the SSO logout will be handled by back end
          // front end only handles it in the old WAM token flow
          if (isSSO()) {
            const logoutUrl = getSSOLogoutLink(Language.getLocale());

            if (logoutUrl && !stayOnTheSamePage) {
              return (window.location.href = logoutUrl);
            }
          } else if (logoutRedirectUrl && !stayOnTheSamePage) {
            return (window.location.href = logoutRedirectUrl);
          }
        } catch (err: any) {
          AppLogger.logWithOptions({
            message: 'Error during logout process',
            level: LogTypes.Error,
            error: err,
            shouldLogToDatadog: true,
            shouldLogToDynatrace: true,
          });
        } finally {
          // If we are logged in then AuthClient.signOut handles the redirect
          // but if not we should handle the redirect here
          if (!stayOnTheSamePage) {
            // for example if this logout was just a cleanup before going to Sign Up
            // then we don't want to change page

            goTo('/', queryParams, { stateParams: { isLoggingOut: true } });
          }
        }
      }
    } finally {
      /*
       * Always reset the flag, even if errors occur during logout.
       * The delay is because when the session times out, multiple 401s can occur in quick succession
       * and they may be sequential so one completes before the next one starts.
       */
      setTimeout(() => {
        canLogout = true;
      }, 3000);
    }
  };
};
